import React, {useContext} from "react";
import {Button, Dialog, DialogContent, DialogTitle, FormControlLabel, Grid,
    IconButton, Switch, Typography, useTheme} from "@mui/material";
import {CloseRounded} from "@mui/icons-material";
import {FormProvider, useForm, useWatch} from "react-hook-form";
import {useMutation} from "@tanstack/react-query";
import {useTranslation} from "react-i18next";
import {useHasScope} from "../../../../config/globalConfig";
import {useNavigate} from "react-router-dom";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {IRecoveryEditGrantsForm} from "../../models/models";
import {RECOVERY_EDIT_GRANTS_FORM_INITIAL_VALUES} from "../config";
import {fetchEnableRecoveryGrants} from "../services/http.services";
import {snackbarTypes} from "../../../../util/state/globalVariables";
import CustomLoader from "../../../../util/component/CustomLoader";
import {FormAutocomplete} from "../../common/form-fields-wrappers";

const EditGrants = (props: any) => {
    const hasScope = useHasScope;
    const theme = useTheme();
    const {t} = useTranslation(['network', 'translation']);
    const {dispatchGlobalData} = useContext(GlobalContext);

    const {open, onClose, onSave, recoveryLocations} = props;

    const recoveryEditGrantsForm = useForm<IRecoveryEditGrantsForm>({
        defaultValues: RECOVERY_EDIT_GRANTS_FORM_INITIAL_VALUES
    });

    const dms = useWatch<IRecoveryEditGrantsForm>({
        name: 'dms',
        control: recoveryEditGrantsForm.control
    });

    const viewErrorMessage = (message: string, type = snackbarTypes.error) => {
        dispatchGlobalData({
            type: 'SET_SNACKBAR',
            payload: {
                snackbar: {
                    message: t(message),
                    type: type,
                    show: true,
                    autoClose: true,
                }
            }
        });
    }

    const handleClose = (e: React.BaseSyntheticEvent) => {
        e.stopPropagation();

        onClose();
    }

    const handleSave = (data: any) => {
        const recoveryLocations = data.dms.map((elem: any) => {
            return {
                bpCode: elem.bpCode,
                flagBookingRecovery: data.flagBookingRecovery,
                flagRaRecovery: data.flagRaRecovery,
                flagCheckInRecovery: data.flagCheckInRecovery,
                flagSignRecovery: data.flagSignRecovery,
                flagBookingSubstitutionRecovery: data.flagBookingSubstitutionRecovery
            }
        });
        dmsEnableRecoveryGrantsUpdateMutation.mutate({recoveryLocations} as any);
    }

    const handleSwitchGrantsOnChange = (e: React.BaseSyntheticEvent, field: 'flagRaRecovery' | 'flagBookingRecovery' | 'flagCheckInRecovery' | 'flagSignRecovery' | 'flagBookingSubstitutionRecovery') => {
        recoveryEditGrantsForm.setValue(field, e.target.value === 'on');
    }

    const dmsEnableRecoveryGrantsUpdateMutation = useMutation(
        fetchEnableRecoveryGrants,
        {
            onSuccess: (response: any) => {
                recoveryEditGrantsForm.reset();
                onSave();
            },
            onError: (err: any) => {
                viewErrorMessage('customerData.message.error.tryLater');
            }
        })

    return (
        <Dialog onClose={handleClose} open={open} fullWidth maxWidth="md">
            <DialogTitle sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                <Typography variant={"bodyLarge"}>
                    {t('enableRecovery.grantsDialog.header.title')}
                </Typography>

                {
                    onClose &&
                    <IconButton aria-label="close" onClick={handleClose}>
                        <CloseRounded/>
                    </IconButton>
                }
            </DialogTitle>

            <DialogContent>
                <FormProvider {...recoveryEditGrantsForm}>
                    <Grid container columnSpacing={2}>
                        <Grid item xs={12} sx={{mb: theme.spacing(1)}}>
                            <FormAutocomplete<IRecoveryEditGrantsForm>
                                name="dms"
                                label={t('enableRecovery.grantsDialog.form.label.dms')}
                                options={recoveryLocations}
                                isOptionEqualToValue={(option, value) => option?.bpCode === value?.bpCode}
                                getOptionLabel={(option) => option.dmsName}
                                multiple
                            />
                        </Grid>
                        <Grid item xs={6} xl={3}>
                            <FormControlLabel sx={{ml: theme.spacing(0.5), my: theme.spacing(2)}}
                                              control={
                                                  <Switch
                                                      size={"medium"}
                                                      name={"flagBookingRecovery"}
                                                      color={'accent'}
                                                      onChange={(e: React.BaseSyntheticEvent) => handleSwitchGrantsOnChange(e, 'flagBookingRecovery')}
                                                      sx={{mr: theme.spacing(1)}}
                                                  />
                                              }
                                              label={t('enableRecovery.grantsDialog.form.label.bookingRecovery')}/>
                        </Grid>

                        <Grid item xs={6} xl={3}>
                            <FormControlLabel sx={{ml: theme.spacing(0.5), my: theme.spacing(2)}}
                                              control={
                                                  <Switch
                                                      size={"medium"}
                                                      name={"flagRaRecovery"}
                                                      color={'accent'}
                                                      onChange={(e: React.BaseSyntheticEvent) => handleSwitchGrantsOnChange(e, 'flagRaRecovery')}
                                                      sx={{mr: theme.spacing(1)}}
                                                  />
                                              }
                                              label={t('enableRecovery.grantsDialog.form.label.raRecovery')}/>
                        </Grid>

                        <Grid item xs={6} xl={3}>
                            <FormControlLabel sx={{ml: theme.spacing(0.5), my: theme.spacing(2)}}
                                              control={
                                                  <Switch
                                                      size={"medium"}
                                                      name={"flagCheckInRecovery"}
                                                      color={'accent'}
                                                      onChange={(e: React.BaseSyntheticEvent) => handleSwitchGrantsOnChange(e, 'flagCheckInRecovery')}
                                                      sx={{mr: theme.spacing(1)}}
                                                  />
                                              }
                                              label={t('enableRecovery.grantsDialog.form.label.checkInRecovery')}/>
                        </Grid>

                        <Grid item xs={6} xl={3}>
                            <FormControlLabel sx={{ml: theme.spacing(0.5), my: theme.spacing(2)}}
                                              control={
                                                  <Switch
                                                      size={"medium"}
                                                      name={"flagSignRecovery"}
                                                      color={'accent'}
                                                      onChange={(e: React.BaseSyntheticEvent) => handleSwitchGrantsOnChange(e, 'flagSignRecovery')}
                                                      sx={{mr: theme.spacing(1)}}
                                                  />
                                              }
                                              label={t('enableRecovery.grantsDialog.form.label.signRecovery')}/>
                        </Grid>

                        <Grid item xs={6} xl={3}>
                            <FormControlLabel sx={{ml: theme.spacing(0.5), my: theme.spacing(2)}}
                                              control={
                                                  <Switch
                                                      size={"medium"}
                                                      name={"flagBookingSubstitutionRecovery"}
                                                      color={'accent'}
                                                      onChange={(e: React.BaseSyntheticEvent) => handleSwitchGrantsOnChange(e, 'flagBookingSubstitutionRecovery')}
                                                      sx={{mr: theme.spacing(1)}}
                                                  />
                                              }
                                              label={t('enableRecovery.grantsDialog.form.label.bookingSubstitutionRecovery')}/>
                        </Grid>

                        <Grid item xs={12} sx={{display: "flex", justifyContent: "flex-end", pt: theme.spacing(1)}}>
                            <Button variant={'contained'} color={'accent'} disabled={!dms || dms?.length === 0} onClick={recoveryEditGrantsForm.handleSubmit(handleSave)}>
                                {t('enableRecovery.grantsDialog.button.save')}
                            </Button>
                        </Grid>
                    </Grid>
                </FormProvider>

                <CustomLoader loading={dmsEnableRecoveryGrantsUpdateMutation.isLoading} />

            </DialogContent>
        </Dialog>
    )
}

export default EditGrants;